import { useMemo } from 'react';

import {
  ExternalLinkIcon,
  GoalIcon,
  InfoIcon,
  MessagesSquareIcon,
  ShieldCheckIcon,
} from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FlowChartIcon } from '@/components/atoms/Icons/FlowChartIcon';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import { RichRadioGroup, RichRadioItem } from '@/components/ui/rich-radio';
import { Webhook, WebhookType } from '@/shared/types/webhook';
import { WEBHOOKS_DOCS_URL } from '@/util/constants';

import { formatTestPayload } from '../components/webhookJsonUtils';
import { WebhookWizardFormValues } from '../wizardForm';

const OPTION_TO_WEBHOOK_TYPE = {
  action: WebhookType.ACTION,
  first_message: WebhookType.FIRST_MESSAGE,
  pre_message: WebhookType.PRE_MESSAGE,
  post_message: WebhookType.POST_MESSAGE,
  pre_nba: WebhookType.PRE_NBA,
  auth_validation: WebhookType.AUTH_VALIDATION,
  auth_pre_enter: WebhookType.AUTH_PRE_ENTER,
} as const;

interface SelectTypeProps {
  existingWebhooks?: Webhook[];
}

export const SelectType = ({ existingWebhooks }: SelectTypeProps) => {
  const { control, setValue } = useFormContext<WebhookWizardFormValues>();
  const { t } = useTranslation();

  const hasExistingWebhook = (type: WebhookType) => {
    return existingWebhooks?.some((wh) => wh.type === type);
  };

  const groups = useMemo(
    () => [
      {
        id: 'dialog',
        title: t('webhook.type_group_dialog'),
        options: [
          {
            value: 'action' as const,
            title: t('webhook.type_dialog_title'),
            description: t('webhook.type_dialog_description'),
            icon: <FlowChartIcon className="h-5 w-5 text-muted-foreground" />,
          },
        ],
      },
      {
        id: 'events',
        title: t('webhook.type_group_event'),
        options: [
          {
            value: 'first_message' as const,
            title: t('webhook.type_first_message_title'),
            description: t('webhook.type_first_message_description'),
            icon: <MessagesSquareIcon className="h-5 w-5 text-muted-foreground" />,
          },
          {
            value: 'pre_message' as const,
            title: (
              <div className="flex items-center gap-1">
                <span>{t('webhook.type_pre_message_title')}</span>
                <InfoTooltip
                  content={t('webhook.type_pre_message_tooltip', {
                    defaultValue:
                      'It won’t trigger for the first message of the conversation if first message webhook is active',
                  })}
                  side="right"
                  size="large"
                >
                  <InfoIcon className="h-4 w-4 text-muted-foreground" />
                </InfoTooltip>
              </div>
            ),
            description: t('webhook.type_pre_message_description'),
            icon: <MessagesSquareIcon className="h-5 w-5 text-muted-foreground" />,
          },
          {
            value: 'post_message' as const,
            title: t('webhook.type_post_message_title'),
            description: t('webhook.type_post_message_description'),
            icon: <MessagesSquareIcon className="h-5 w-5 text-muted-foreground" />,
          },
        ],
      },
      {
        id: 'campaigns',
        title: t('webhook.type_group_campaigns'),
        options: [
          {
            value: 'pre_nba' as const,
            title: t('webhook.type_pre_nba_title'),
            description: t('webhook.type_pre_nba_description'),
            icon: <GoalIcon className="h-5 w-5 text-muted-foreground" />,
          },
        ],
      },
      {
        id: 'auth',
        title: t('webhook.type_group_auth'),
        options: [
          {
            value: 'auth_validation' as const,
            title: t('webhook.type_auth_validation_title'),
            description: t('webhook.type_auth_validation_description'),
            icon: <ShieldCheckIcon className="h-5 w-5 text-muted-foreground" />,
          },
          {
            value: 'auth_pre_enter' as const,
            title: t('webhook.type_auth_pre_enter_title'),
            description: t('webhook.type_auth_pre_enter_description'),
            icon: <ShieldCheckIcon className="h-5 w-5 text-muted-foreground" />,
          },
        ],
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto w-full max-w-[570px]">
      <div className="rounded-lg border border-border bg-background px-8 pt-8 pb-12">
        <FormField
          control={control}
          name="type"
          rules={{ required: { value: true, message: t('validation.required') } }}
          render={({ field }) => (
            <FormItem className="space-y-6">
              <RichRadioGroup
                ref={field.ref}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  const selectedType =
                    OPTION_TO_WEBHOOK_TYPE[value as keyof typeof OPTION_TO_WEBHOOK_TYPE];
                  setValue('testPayload', formatTestPayload(selectedType), {
                    shouldDirty: false,
                    shouldTouch: false,
                    shouldValidate: false,
                  });
                }}
                onBlur={field.onBlur}
                className="gap-5"
              >
                {groups.map((group) => (
                  <section key={group.id} aria-labelledby={`${group.id}-heading`}>
                    <div
                      className="space-y-1 text-sm font-semibold text-foreground"
                      id={`${group.id}-heading`}
                    >
                      {group.title}
                    </div>
                    <div className="mt-2 grid gap-3">
                      {group.options.map((option) => {
                        const webhookType = OPTION_TO_WEBHOOK_TYPE[option.value];
                        const allowsManyPerBrain =
                          webhookType === WebhookType.ACTION ||
                          webhookType === WebhookType.AUTH_VALIDATION ||
                          webhookType === WebhookType.AUTH_PRE_ENTER;
                        const isDisabled = !allowsManyPerBrain && hasExistingWebhook(webhookType);

                        return (
                          <RichRadioItem
                            key={option.value}
                            value={option.value}
                            title={option.title}
                            description={option.description}
                            icon={option.icon}
                            disabled={isDisabled}
                            badge={
                              isDisabled ? (
                                <Badge rounded={false} type="secondary" flavour="successful">
                                  {t('common.added')}
                                </Badge>
                              ) : undefined
                            }
                            className="w-full max-w-none"
                          />
                        );
                      })}
                    </div>
                  </section>
                ))}
              </RichRadioGroup>
              <FormMessage />

              <div className="flex items-center">
                <Button asChild variant="secondary">
                  <a href={WEBHOOKS_DOCS_URL} target="_blank" rel="noopener noreferrer">
                    {t('webhook.learn_more')}
                    <ExternalLinkIcon />
                  </a>
                </Button>
              </div>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default SelectType;
